import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Button, Checkbox, Dropdown, Form, Header, Menu, Modal, Tab } from "semantic-ui-react";

import { DBB_CONTRACT_TYPE } from "./../../../../modules/Appointments/common";
import AppService from "./../../../../modules/Appointments/service";
import DBBContractOptions from "./DBBContractOptions";
import DBBCarSection from "./components/DBBCarSection";
import DBBCustomerSection from "./components/DBBCustomerSection";
import DBBTyreSetsSection from "./components/DBBTyreSetsSection";
import {
  getContractFormErrors,
  getContractSections,
  getContractTypeOptions,
  getDefaultTyreSets,
  getIncompleteCustomerSections,
  toContractCustomer,
  toCreateContractPayload,
} from "./utils";
import "./DBBCreateContractModal.css";

const getDefaultValues = (car, customers, lastAppointment) => {
  return {
    contract_type: customers?.contract?.is_lease_company ? DBB_CONTRACT_TYPE.LEASE : DBB_CONTRACT_TYPE.PRIVATE,
    pickup_by_dbb: false,
    lease_company_id: null,
    storage_location_id: null,
    driver: toContractCustomer(customers?.driver, "driver"),
    owner: toContractCustomer(customers?.owner, "owner"),
    is_driver_unknown: false,
    is_owner_unknown: false,
    car: {
      reg_nr: car.reg_nr || "",
      vin_nr: car.vin_nr || "",
      make: car.make || "",
      model: car.model || "",
      model_year: null,
      mileage: lastAppointment?.current_km || null,
    },
    tyre_sets: getDefaultTyreSets(car),
  };
};

class DBBCreateContractModal extends Component {
  state = {
    values: getDefaultValues(this.props.car, this.props.customers, this.props.lastAppointment),
    leaseCompanyOptions: [],
    storageLocationOptions: [],
    isFetchingOptions: false,
    isSubmitted: false,
    isSaving: false,
  };

  componentDidMount() {
    this.getContractOptions();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open || !this.props.open) return;

    this.setState({ values: getDefaultValues(this.props.car, this.props.customers, this.props.lastAppointment), isSubmitted: false });
  }

  getContractOptions = () => {
    this.setState({ isFetchingOptions: true }, () => {
      DBBContractOptions.getContractOptions()
        .then(options => this.setState({ ...options, isFetchingOptions: false }))
        .catch(error => {
          this.setState({ isFetchingOptions: false });
          console.warn("Error getting DBB contract options", error);
        });
    });
  };

  setValue = (field, value) => this.setState(state => ({ values: { ...state.values, [field]: value } }));

  handleChangeCustomerField = (customerField, field, value) =>
    this.setState(state => ({ values: { ...state.values, [customerField]: { ...state.values[customerField], [field]: value } } }));

  handleChangeCustomerRole = (customerField, role) => this.setValue(customerField, toContractCustomer(this.props.customers?.[role], role));

  handleToggleUnknown = (customerField, unknownField) =>
    this.setState(state => ({
      values: { ...state.values, [unknownField]: !state.values[unknownField], [customerField]: state.values[unknownField] ? state.values[customerField] : undefined },
    }));

  handleChangeCarField = (field, value) => this.setState(state => ({ values: { ...state.values, car: { ...state.values.car, [field]: value } } }));

  handleChangeSetTyres = (setIndex, tyres) =>
    this.setState(state => ({
      values: { ...state.values, tyre_sets: state.values.tyre_sets.map((set, index) => (index === setIndex ? { ...set, tyres } : set)) },
    }));

  handleChangeWheelType = (setIndex, wheelType) =>
    this.setState(state => ({
      values: { ...state.values, tyre_sets: state.values.tyre_sets.map((set, index) => (index === setIndex ? { ...set, wheel_type: wheelType } : set)) },
    }));

  getCustomerPanes = sections => {
    const { customers, t } = this.props;
    const { values, isSubmitted } = this.state;

    const incompleteCustomerFields = isSubmitted ? getIncompleteCustomerSections(values) : [];

    return sections.map(({ customerField, unknownField, label, defaultText }) => ({
      menuItem: (
        <Menu.Item key={customerField} className={incompleteCustomerFields.includes(customerField) ? "has-error" : undefined}>
          {t(label).message || defaultText}
        </Menu.Item>
      ),
      render: () => (
        <DBBCustomerSection
          customers={customers}
          customer={values[customerField]}
          isUnknown={values[unknownField]}
          hasValidationError={isSubmitted}
          onChangeRole={role => this.handleChangeCustomerRole(customerField, role)}
          onChangeField={(field, value) => this.handleChangeCustomerField(customerField, field, value)}
          onToggleUnknown={() => this.handleToggleUnknown(customerField, unknownField)}
        />
      ),
    }));
  };

  isFormInvalid = () => {
    const { onError, t } = this.props;
    const { hasIncompleteCar, hasIncompleteCustomer, hasMissingLeaseCompany, hasMissingStorageLocation, hasIncompleteTyreSets } = getContractFormErrors(
      this.state.values
    );

    if (hasIncompleteCar || hasIncompleteCustomer || hasMissingLeaseCompany || hasMissingStorageLocation) return true;

    if (hasIncompleteTyreSets) {
      onError(t("dbb_incomplete_tyre_sets").message || "Pick two complete tyre sets, each one with a tyre on all four positions");
      return true;
    }

    return false;
  };

  handleSave = () => {
    const { car, onCreated, onError, onClose } = this.props;

    this.setState({ isSubmitted: true }, () => {
      if (this.isFormInvalid()) return;

      this.setState({ isSaving: true }, () => {
        AppService.createDBBContract(toCreateContractPayload(car, this.state.values))
          .then(result => {
            this.setState({ isSaving: false });

            if (!result?.data?.data) return onError("An unknown error occured, please try again");

            onCreated();
            onClose();
          })
          .catch(error => {
            this.setState({ isSaving: false });
            const errorMsg = error.response?.data?.errors?.[0] || "Error creating DBB contract";
            onError(errorMsg);
            console.warn("Error creating DBB contract", error);
          });
      });
    });
  };

  render() {
    const { car, location, open, onClose, t } = this.props;
    const { values, leaseCompanyOptions, storageLocationOptions, isFetchingOptions, isSubmitted, isSaving } = this.state;

    const sections = getContractSections(values.contract_type);
    const isLeaseContract = values.contract_type === DBB_CONTRACT_TYPE.LEASE;

    return (
      <Modal size="large" open={open} onClose={onClose} closeOnDimmerClick={false} className="DBBCreateContractModal">
        <Modal.Header>{t("create_dbb_contract").message || "Create DBB contract"}</Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <div className="contract-type-section">
              <Header as="h5" dividing>
                {t("contract").message || "Contract"}
              </Header>
              <Form.Group>
                <Form.Field width={6}>
                  <label>{t("contract_type").message || "Contract type"}</label>
                  <Dropdown
                    selection
                    fluid
                    selectOnBlur={false}
                    value={values.contract_type}
                    options={getContractTypeOptions(t)}
                    onChange={(_e, { value }) => this.setValue("contract_type", value)}
                  />
                </Form.Field>
                {isLeaseContract && (
                  <Form.Field width={6} required error={isSubmitted && !values.lease_company_id}>
                    <label>{t("lease_company").message || "Lease company"}</label>
                    <Dropdown
                      selection
                      fluid
                      search
                      selectOnBlur={false}
                      loading={isFetchingOptions}
                      placeholder={t("lease_company").message || "Lease company"}
                      options={leaseCompanyOptions}
                      value={values.lease_company_id}
                      onChange={(_e, { value }) => this.setValue("lease_company_id", value)}
                    />
                  </Form.Field>
                )}
              </Form.Group>

              <Form.Group>
                <Form.Field width={6}>
                  <Checkbox
                    label={t("pickup_by_dbb").message || "Pickup by DBB"}
                    checked={values.pickup_by_dbb}
                    onChange={(_e, { checked }) => this.setValue("pickup_by_dbb", checked)}
                  />
                </Form.Field>
              </Form.Group>
            </div>

            <div className="contract-customers-section">
              <Header as="h5" dividing>
                {t("customer").message || "Customer"}
              </Header>

              <Tab menu={{ secondary: true }} panes={this.getCustomerPanes(sections)} />
            </div>

            <div className="contract-car-section">
              <Header as="h5" dividing>
                {t("dbb_car").message || "Car"}
              </Header>
              <DBBCarSection car={values.car} hasValidationError={isSubmitted} onChangeField={this.handleChangeCarField} />
            </div>

            <div className="contract-tyre-sets-section">
              <Header as="h5" dividing>
                {t("tyre_sets").message || "Tyre sets"}
              </Header>
              <DBBTyreSetsSection
                car={car}
                location={location}
                tyreSets={values.tyre_sets}
                hasValidationError={isSubmitted}
                onChangeSetTyres={this.handleChangeSetTyres}
                onChangeWheelType={this.handleChangeWheelType}
                storageLocationId={values.storage_location_id}
                storageLocationOptions={storageLocationOptions}
                isFetchingOptions={isFetchingOptions}
                onChangeStorageLocation={value => this.setValue("storage_location_id", value)}
              />
            </div>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button color="grey" onClick={onClose}>
            {t("cancel").message || "Cancel"}
          </Button>
          <Button color="green" onClick={this.handleSave} disabled={isSaving} loading={isSaving}>
            {t("save").message || "Save"}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

export default withTranslation()(DBBCreateContractModal);
